import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trash2, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getComments, createComment, deleteComment } from "../lib/services";
import { formatApiError } from "../lib/api";
import ReportModal from "./ReportModal";

export default function CommentThread({ logId, open, onCountChange }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reporting, setReporting] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await getComments(logId);
      setItems(r.items || []);
      onCountChange?.(r.count);
    } catch (err) {
      console.warn("comments fetch failed:", err);
    } finally {
      setLoaded(true);
    }
  }, [logId, onCountChange]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  if (!open) return null;

  const post = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      await createComment(logId, body);
      setText("");
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (commentId) => {
    try {
      await deleteComment(logId, commentId);
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  return (
    <div data-testid={`comments-${logId}`} className="mt-3 border-t border-white/10 pt-3 space-y-3">
      {!loaded ? (
        <p className="font-mono text-xs text-white/40">loading...</p>
      ) : items.length === 0 ? (
        <p className="font-mono text-[11px] text-white/40">no comments yet.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => {
            const isMine = user?.id === c.userId;
            const canDelete = isMine || user?.role === "admin";
            return (
              <li
                key={c.id}
                data-testid={`comment-${c.id}`}
                className="font-mono text-xs border-l-2 border-white/15 pl-2.5"
              >
                <div className="flex items-baseline justify-between gap-2">
                  {c.user?.username ? (
                    <Link
                      to={`/user/${c.user.username}`}
                      className="text-acid hover:underline"
                    >
                      @{c.user.username}
                    </Link>
                  ) : (
                    <span className="text-acid">@user</span>
                  )}
                  <span className="text-white/30 text-[10px]">
                    {(c.createdAt || "").slice(0, 10)}
                  </span>
                </div>
                <div className="text-white/85 mt-0.5 whitespace-pre-wrap break-words">
                  {c.text}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-white/40">
                  {canDelete && (
                    <button
                      data-testid={`comment-delete-${c.id}`}
                      onClick={() => remove(c.id)}
                      className="inline-flex items-center gap-1 hover:text-white"
                      aria-label="delete"
                    >
                      <Trash2 className="w-3 h-3" /> delete
                    </button>
                  )}
                  {!isMine && (
                    <button
                      data-testid={`comment-report-${c.id}`}
                      onClick={() => setReporting({ id: c.id, username: c.user?.username })}
                      className="inline-flex items-center gap-1 hover:text-white"
                      aria-label="report"
                    >
                      <Flag className="w-3 h-3" /> report
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={post} data-testid={`comment-form-${logId}`} className="flex gap-2">
        <input
          data-testid={`comment-input-${logId}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="say something (500 max)"
          maxLength={500}
          className="flex-1 bg-transparent border-b-2 border-white/30 px-1 py-2 font-mono text-sm focus:border-acid outline-none"
        />
        <button
          type="submit"
          data-testid={`comment-submit-${logId}`}
          disabled={submitting || !text.trim()}
          className="font-mono text-[11px] uppercase tracking-wider bg-acid text-black px-3 py-1.5 border-2 border-acid hover:bg-black hover:text-acid transition-colors disabled:opacity-40"
        >
          post
        </button>
      </form>

      <ReportModal
        open={!!reporting}
        onClose={() => setReporting(null)}
        targetType="comment"
        targetId={reporting?.id}
        label={reporting?.username ? `comment by @${reporting.username}` : "comment"}
      />
    </div>
  );
}
